import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { IconButton, Skeleton, SvgIcon, TableSortLabel, Typography, useTheme } from '@mui/material';
import useLogError from '../../../../hooks/useLogError';
import CustomPagination from '../../../../components/forms/Pagination';
import { Trash01 } from '../../../../assets';
import CreateUpdateRateModal from './CreateUpdateRateModal';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { client, PaginatedList, CurrencyConversionContent, ExchangeRateRequest } from '../../../../app/services/api/orderManagementClient';
import { ConfirmationDialog } from '../../../../components/shared/dialogs/ConfirmationDialog';
import { UserContext } from '../../../../components/shared/useUser';
import { ExchangeRateTab } from '../enums/ExchangeRateTab';

interface UsersTableProps {
  list: PaginatedList<CurrencyConversionContent>;
  loadData: () => void;
  isLoading: boolean;
  clearAll: () => void;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  onCreate: () => void;
  activeTab: number;
  setShowDeleteSuccessMessage: React.Dispatch<React.SetStateAction<boolean>>;
  setShowUpdateSuccessMessage: React.Dispatch<React.SetStateAction<boolean>>;
}

const ExchangeRatesTable = ({ list, loadData, isLoading, clearAll, page, setPage,
  onCreate, activeTab, setShowUpdateSuccessMessage, setShowDeleteSuccessMessage }: UsersTableProps) => {
  const { t } = useTranslation('manageExchangeRates');
  const { logError } = useLogError();
  const theme = useTheme();
  const [data, setData] = React.useState(list);
  const { selectedStore } = React.useContext(UserContext);
  const [selectedRate, setSelectedRate] = React.useState<CurrencyConversionContent | undefined>(undefined);
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof CurrencyConversionContent>('ConversionDate');
  const rowsPerPage = 10;
  const maxPages = list
    ? Math.max(1, Math.ceil(list.totalPages / rowsPerPage))
    : 1;

  React.useEffect(() => {
    setData(list);
    handleSort('ConversionDate');
  }, [list]);

  const parseDotNetDate = (dateString: string) => {
    if (!dateString) return '';
    const match = /\/Date\((\d+)([+-]\d+)?\)\//.exec(dateString);
    if (!match) return dateString;
    const timestamp = parseInt(match[1], 10);
    return new Date(timestamp).toLocaleDateString();
  };

  const visibleRows = () => {
    return data.items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  const handleSort = (property: keyof CurrencyConversionContent) => {
    const isAsc = orderBy === property && order === 'asc';
    if (data) {
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property as keyof CurrencyConversionContent);
    }
  };

  const onEdit = (rate: CurrencyConversionContent | undefined) => {
    if (rate) {
      setSelectedRate(rate);
    }
    setShowEditModal(true);
  };

  const handleEdit = async (body: ExchangeRateRequest) => {
    try {
      const result = await client.updateExchangeRate(selectedStore?.storeNumber, body);
      if (result.success) {
        setShowUpdateSuccessMessage(true);
        setShowEditModal(false);
        loadData();
      } else {
        logError(result.message);
      }
    } catch (error) {
      logError(error);
    }
  };

  const onDelete = (rate: CurrencyConversionContent) => {
    setSelectedRate(rate);
    setShowDeleteModal(true);
  };

  const handleDelete = () => {
    client.deleteExchangeRate(selectedStore?.storeNumber, selectedRate?.ID ?? 0)
      .then(() => {
        setShowDeleteSuccessMessage(true);
        loadData();
      })
      .catch(logError);

    setShowDeleteModal(false);
    setSelectedRate(undefined);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedRate(undefined);
  };

  if (data.items.length == 0) {
    return <EmptyTable
      onAddIsCreating={false}
      text={t('exchangeRates')}
      searchTerm={undefined}
      onClear={clearAll}
      onAdd={onCreate}
      addItemText={t('newRate')} />;
  }

  return (
    <>
      {isLoading ? <Skeleton sx={{ bgcolor: theme.palette.custom.gray[100] }}
        variant='rectangular'
        height={445.89} />
        :
        <TableContainer sx={{ borderRadius: '12px' }}
          component='div'>
          <Table size="small">
            <TableHead sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'ToCurrency'}
                    direction={order}
                    onClick={() => handleSort('ToCurrency')}
                  >
                    {t('currency')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'ConversionRate'}
                    direction={order}
                    onClick={() => handleSort('ConversionRate')}
                  >
                    {t('exchangeRate')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'ConversionDate'}
                    direction={order}
                    onClick={() => handleSort('ConversionDate')}
                  >
                    {t('fromDate')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows().map((row) => (
                <TableRow
                  key={row.ID}
                  onClick={() => { activeTab !== ExchangeRateTab.HISTORY ? onEdit(row) : null; }}
                  sx={activeTab !== ExchangeRateTab.HISTORY ? { cursor: 'pointer' } : null}
                >
                  <TableCell>
                    <Typography variant='textXS'
                      fontWeight={600}
                      color={theme.palette.primary[500]}
                      sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      {row.ToCurrency}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS'>
                      {row.ConversionRate}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS'>
                      {parseDotNetDate(row.ConversionDate)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>
                    <IconButton aria-label={t('deleteModal.confirm')}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(row);
                      }}
                      sx={{
                        visibility: activeTab !== ExchangeRateTab.HISTORY ? 'visible' : 'hidden',
                      }}
                    >
                      <SvgIcon fontSize='small'><Trash01 color={theme.palette.error[500]} /></SvgIcon>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {/* Add empty rows to maintain height */}
              {Array.from({ length: Math.max(0, 10 - (visibleRows().length ?? 0)) }).map((_, index) => (
                <TableRow key={`empty-row-${index}`}
                  sx={{ height: '40px', backgroundColor: theme.palette.common.white }}>
                  <TableCell colSpan={6}
                    sx={{
                      backgroundColor: theme.palette.common.white,
                      borderColor: index === Math.max(0, 10 - (visibleRows().length ?? 0)) - 1 ? theme.palette.custom.gray[100] : theme.palette.common.white,
                    }} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>}
      <CustomPagination
        page={page}
        setPage={setPage}
        maxPages={maxPages}
        breakpointForChangeDisplay={120000}
      />
      <ConfirmationDialog open={showDeleteModal}
        onCancel={() => handleCloseModal()}
        onConfirm={() => handleDelete()}
        icon={<Trash01 />}
        title={t('deleteModal.confirm')}
        message={t('deleteModal.confirmMessage')}
        confirmButtonText={t('deleteModal.yes')}
        cancelButtonText={t('deleteModal.no')} />
      <CreateUpdateRateModal
        openModal={showEditModal}
        onConfirm={handleEdit}
        selectedRate={selectedRate}
        onCancel={handleCloseModal}
      />
    </>
  );
};

export default ExchangeRatesTable;
